/**
 * How a value READS — resolved once, emitted twice.
 *
 * The codebase already fought this battle over decimals and won it:
 * `FORMAT_DECIMALS` lives in this package because `apps/api` cannot import
 * `report-kit`, so a copy in each was a copy nothing could hold together — and
 * the failure it lets through is a column reading `1,234.56` on screen and
 * `1,234.6` in the file it was exported from.
 *
 * Every display attribute has that same failure mode, so all of them resolve
 * here and both surfaces consume the result:
 *
 *  - {@link resolveValueFormat} flattens the three layers (the unit's own
 *    precision, the model author's override, the report author's choice) into
 *    one {@link ResolvedFormat}.
 *  - {@link intlOptionsFor} turns that into `Intl.NumberFormat` options, for
 *    the screen, the charts and the metric tiles.
 *  - {@link excelPatternFor} turns the SAME object into a number-format
 *    string, for both spreadsheet writers.
 *
 * The two emitters sit side by side deliberately: teaching one a new attribute
 * and forgetting the other is the whole bug class, and here that omission is
 * visible in one screen of a diff. `FORMAT_EXPECTATIONS` is the belt to that
 * braces — a table both packages' tests assert against, since nothing else can
 * span them.
 *
 * No SQL, no React, no driver: a plain object in, an options bag and a string
 * out. See docs/reporting/excel-export.md.
 */
import {
  decimalsFor,
  FORMAT_DECIMALS,
  type Measure,
  type MeasureFormat,
  type MeasureSource,
} from './model';
import {
  type FormatRule,
  isRedStyle,
  type NegativeStyle,
  type RuleTone,
  type ScaleStyle,
  type ShowAsMode,
  type SymbolStyle,
  showAsFor,
  type UIReportSpec,
  type ValueFormat,
  valueFormatFor,
  type ZeroStyle,
} from './spec';

/** The keys of {@link ValueFormat} a control may set. */
export type ValueFormatKey = keyof ValueFormat;

/**
 * Which attributes mean anything for which unit.
 *
 * A currency symbol on an hours measure is a control that cannot do anything,
 * and a stored key that cannot do anything must be IGNORED rather than obeyed
 * — a spec can arrive hand-edited, or saved before a model author changed a
 * measure's format. The builder reads the same table to decide what to draw,
 * so the panel and the resolver cannot disagree about what applies.
 */
export const FORMAT_CONTROLS: Record<MeasureFormat, readonly ValueFormatKey[]> = {
  // `scale` (K / M) is CURRENCY ONLY, for now. `$1.2M` is how money is read;
  // hours and counts in this app sit in the tens and hundreds, where every
  // cell would print `0.0K`. A stored scale on any other unit is ignored like
  // any key that does not apply, so widening this later needs no migration.
  number: ['decimals', 'separators', 'negative', 'zero', 'rules'],
  currency: ['decimals', 'scale', 'symbol', 'accounting', 'negative', 'zero', 'rules'],
  percent: ['decimals', 'negative', 'zero', 'rules'],
  hours: ['decimals', 'separators', 'negative', 'zero', 'rules'],
};

/**
 * Accounting fixes what it fixes.
 *
 * Negatives are always bracketed and zero is always a hyphen — that IS the
 * format, not a pair of choices made inside it — so neither control is offered
 * and neither stored value is read.
 *
 * `rules` SURVIVES it, where `negative` and `zero` do not: a highlight paints
 * the cell behind the number and says nothing about how the number is written,
 * so accounting has no opinion on it to override.
 */
export const ACCOUNTING_CONTROLS: readonly ValueFormatKey[] = [
  'decimals',
  'scale',
  'symbol',
  'accounting',
  'rules',
];

/** Everything either emitter needs, with every fallback already applied. */
export interface ResolvedFormat {
  /** The UNIT, after `showAs` has had its say. Accounting resolves to currency. */
  format: MeasureFormat;
  /** Currency with the symbol and decimal point aligned down the column. */
  accounting: boolean;
  decimals: number;
  negative: NegativeStyle;
  /** Thousands separators. */
  separators: boolean;
  /** Display divisor; `none` reads the value whole. See {@link SCALES}. */
  scale: ScaleStyle | 'none';
  zero: ZeroStyle;
  symbol: SymbolStyle;
  /**
   * Highlight rules, in the author's order — first match wins.
   *
   * Empty when none apply, so every reader is a loop rather than a null check.
   */
  rules: readonly FormatRule[];
  /** Which attributes applied. The builder draws exactly these. */
  controls: readonly ValueFormatKey[];
  /** The `showAs` in force, so a caller can tell a share from a quantity. */
  mode: ShowAsMode;
}

/**
 * Resolve one measure's display attributes against the spec it is read in.
 *
 * The three layers, outermost last:
 *
 *  1. `FORMAT_DECIMALS` — what the unit is read to.
 *  2. `Measure.decimals` — the MODEL AUTHOR's override, for a measure whose
 *     quantity genuinely differs from its unit. Dropped under a `showAs`,
 *     because it describes the measure's own unit and a share is not that
 *     unit — the rule `formatCell` has always applied.
 *  3. `spec.valueFormat` — the REPORT AUTHOR's choice. It applies under a
 *     `showAs` too: they are describing the cell in front of them, which is a
 *     percentage, rather than restating a fact about the measure.
 *
 * An absent measure resolves rather than throwing. `validateSpec` has already
 * refused an unknown id upstream, so it is unreachable — and a column rendered
 * as a plain number beats a white screen if it ever is reached.
 */
export function resolveValueFormat(
  measure:
    | Pick<Measure, 'format' | 'decimals'>
    | Pick<MeasureSource, 'format' | 'decimals'>
    | undefined,
  spec: Pick<UIReportSpec, 'showAs' | 'valueFormat'>,
  measureId: string,
): ResolvedFormat {
  const mode = showAsFor(spec, measureId);
  const chosen = valueFormatFor(spec, measureId);

  // The MODE WINS ON STYLE: a cell under any `showAs` carries a ratio whatever
  // the measure is made of, so formatting it as hours prints "0.3" where the
  // screen says "29.4%".
  const unit: MeasureFormat = mode !== 'raw' ? 'percent' : (measure?.format ?? 'number');
  const accounting = unit === 'currency' && chosen.accounting === true;
  const controls = accounting
    ? ACCOUNTING_CONTROLS
    : (FORMAT_CONTROLS[unit] ?? FORMAT_CONTROLS.number);
  const has = (key: ValueFormatKey) => controls.includes(key);

  // The model author's precision, for a raw cell only; then the report
  // author's on top of it. `decimalsFor` is what makes an out-of-range value
  // harmless rather than a `RangeError` on one surface and a malformed
  // spreadsheet on the other.
  const authored = mode === 'raw' ? decimalsFor(unit, measure?.decimals) : FORMAT_DECIMALS[unit];
  const decimals = decimalsFor(unit, chosen.decimals ?? authored);

  return {
    format: unit,
    accounting,
    decimals,
    negative: accounting ? 'parens' : has('negative') ? (chosen.negative ?? 'minus') : 'minus',
    separators: has('separators') ? chosen.separators !== false : true,
    scale: has('scale') ? (chosen.scale ?? 'none') : 'none',
    zero: accounting ? 'dash' : has('zero') ? (chosen.zero ?? 'number') : 'number',
    symbol: has('symbol') ? (chosen.symbol ?? 'symbol') : 'symbol',
    rules: has('rules') ? (chosen.rules ?? []) : [],
    controls,
    mode,
  };
}

/**
 * What each scale divides by, and how each surface spells it.
 *
 * DISPLAY ROUNDING, exactly as a spreadsheet does it: the divisor applies to
 * what is PRINTED, and the cell underneath keeps the whole value. So a column of
 * 1.4K + 1.4K totals 2.8K from the real numbers, a formula over the exported
 * cells is exact, and a highlight threshold is still written in whole units.
 *
 * `excel` is the TRAILING COMMA: in a number format, each comma after the last
 * digit placeholder divides by a thousand. It must sit after the fraction, not
 * before it — `#,##0.0,,"M"` — or Excel reads it as a grouping separator.
 */
export const SCALES: Record<ScaleStyle, { divisor: number; suffix: string; excel: string }> = {
  thousands: { divisor: 1_000, suffix: 'K', excel: ',"K"' },
  millions: { divisor: 1_000_000, suffix: 'M', excel: ',,"M"' },
};

/**
 * Which tone this cell takes, or `undefined` for none.
 *
 * FIRST MATCH WINS — see `rules` on `ValueFormat`. A null, undefined or NaN
 * cell takes no tone at all: those print as an em dash, and painting "no rows
 * matched" as an overspend is the one outcome worse than not painting it.
 *
 * 🔴 THE THRESHOLD IS IN DISPLAY UNITS AND THE VALUE IS NOT. A percent cell
 * carries a ratio, so an author's `100` has to meet `1.0` — the division
 * happens HERE, once, rather than in each of the three surfaces that ask.
 * `f.format` is the RESOLVED unit, so a measure under a `showAs` share gets
 * this treatment whatever it is made of, which is what makes a rule mean the
 * same thing as the number the reader is looking at.
 */
export function toneFor(value: number | null | undefined, f: ResolvedFormat): RuleTone | undefined {
  if (value === null || value === undefined || Number.isNaN(value)) return undefined;
  const scale = f.format === 'percent' ? 100 : 1;
  for (const rule of f.rules) {
    const threshold = rule.value / scale;
    const hit =
      rule.op === 'lt'
        ? value < threshold
        : rule.op === 'lte'
          ? value <= threshold
          : rule.op === 'gt'
            ? value > threshold
            : value >= threshold;
    if (hit) return rule.tone;
  }
  return undefined;
}

/** Does a cell of this value take the negative colour? */
export function readsNegative(value: number | null | undefined, f: ResolvedFormat): boolean {
  return (
    isRedStyle(f.negative) &&
    value !== null &&
    value !== undefined &&
    !Number.isNaN(value) &&
    value < 0
  );
}

/**
 * The screen's half: `Intl.NumberFormat` options.
 *
 * BOTH precision ends are pinned. Padding is the point as much as truncating:
 * a column where 8 reads "8" and 0.55 reads "0.55" is one the eye cannot scan
 * down.
 *
 * The bracket and the colour are NOT here — a formatter returns a string, and
 * a string can carry a bracket but not a colour. See `formatResolved` and
 * {@link readsNegative} in `report-kit`.
 */
export function intlOptionsFor(f: ResolvedFormat): Intl.NumberFormatOptions {
  const options: Intl.NumberFormatOptions = {
    useGrouping: f.separators,
    minimumFractionDigits: f.decimals,
    maximumFractionDigits: f.decimals,
  };
  if (f.format === 'percent') options.style = 'percent';
  if (f.format === 'currency' && f.symbol !== 'none') {
    options.style = 'currency';
    options.currency = 'USD';
    if (f.symbol === 'code') options.currencyDisplay = 'code';
  }
  return options;
}

/** The digit placeholders, fraction and scale — everything but sign and symbol. */
function excelDigits(f: ResolvedFormat): string {
  const grouped = f.separators ? '#,##0' : '0';
  const fraction = f.decimals > 0 ? `.${'0'.repeat(f.decimals)}` : '';
  const scale = f.scale === 'none' ? '' : SCALES[f.scale].excel;
  return `${grouped}${fraction}${scale}`;
}

/** The positive section of an Excel pattern — the part every section is built on. */
function excelBody(f: ResolvedFormat): string {
  const digits = excelDigits(f);
  if (f.format === 'percent') return `${digits}%`;
  if (f.format !== 'currency' || f.symbol === 'none') return digits;
  return `${f.symbol === 'code' ? '"USD "' : '$'}${digits}`;
}

/**
 * The export's half: an Excel number format.
 *
 * Excel reads a pattern as up to four `;`-separated sections — positive,
 * negative, zero, text — POSITIONALLY. Two consequences run through this
 * function and neither is obvious:
 *
 *  - Supplying a negative section OVERRIDES Excel's built-in handling, so a
 *    red minus has to respell the minus it would otherwise have got free.
 *  - Describing zeroes forces the negative section to be spelled out too,
 *    because you cannot reach the third slot without filling the second.
 */
export function excelPatternFor(f: ResolvedFormat): string {
  const body = excelBody(f);

  // ACCOUNTING IS ITS OWN PATTERN. `_(` pads by the width of a bracket and `*`
  // fills to the column edge — that alignment is the entire reason anybody
  // picks the category, and it has no `Intl` equivalent. See
  // `FORMAT_EXPECTATIONS`, which records it as a KNOWN divergence rather than
  // pretending the two surfaces match.
  if (f.accounting) {
    const symbol = f.symbol === 'code' ? '"USD "' : f.symbol === 'none' ? '' : '$';
    const digits = excelDigits(f);
    return `_(${symbol}* ${digits}_);_(${symbol}* (${digits});_(${symbol}* "-"??_);_(@_)`;
  }

  const red = isRedStyle(f.negative) ? '[Red]' : '';
  const negative =
    f.negative === 'redOnly'
      ? `${red}${body}`
      : f.negative === 'parens' || f.negative === 'parensRed'
        ? `${red}(${body})`
        : `${red}-${body}`;

  if (f.zero !== 'number') {
    return `${body};${negative};${f.zero === 'dash' ? '"-"' : ''}`;
  }
  // One section, and Excel's own negative handling, when nothing asked for more.
  return f.negative === 'minus' ? body : `${body};${negative}`;
}

/**
 * The cross-package pin.
 *
 * `apps/api` cannot import `report-kit`, so no test can assert that the screen
 * and the file agree — the shared resolver above IS the enforcement, and this
 * table is what keeps it honest as attributes are added. Each row states the
 * rendered text and the Excel pattern one `ValueFormat` produces;
 * `report-kit`'s tests assert their formatter against `text`, and
 * `apps/api`'s assert `numFmt` against `excel`.
 *
 * **A new attribute is not done until it has rows here.** An attribute that
 * reaches one emitter and not the other is a silent no-op, which is precisely
 * the drift this file exists to prevent.
 *
 * `divergence` names a case where the two surfaces deliberately differ, so a
 * mismatch is recorded rather than discovered in a download.
 */
export interface FormatExpectation {
  what: string;
  measure: Pick<Measure, 'format' | 'decimals'>;
  chosen: ValueFormat;
  /** `[value, rendered]` pairs, in `en-US`. */
  text: readonly (readonly [number | null, string])[];
  excel: string;
  /**
   * `[value, tone]` pairs — which highlight the cell takes, `null` for none.
   *
   * A SECOND AXIS, because a highlight is not in the number-format string.
   * `text` and `excel` describe how the digits are written and a tone paints
   * the cell behind them, so neither field could have carried this and a rule
   * that reached one surface only would have passed the table unnoticed —
   * exactly the drift it exists to catch.
   */
  tones?: readonly (readonly [number, RuleTone | null])[];
  /**
   * The comparison and threshold each rule reaches the writer with, in priority
   * order.
   *
   * Thresholds here are in the CELL's units, not the author's: a percent rule
   * written as `100` arrives as `1`. That conversion is the single most
   * breakable thing about this feature and it is asserted, not described.
   *
   * 🔴 THIS IS THE HAND-OFF, NOT THE BYTES. The writer turns each of these into
   * an `expression` rule wrapped in `NOT(ISBLANK(...))` — Excel reads a blank
   * cell as 0, so an unguarded comparison painted every absent cell. That guard
   * is therefore NOT pinned here; it lives in `spreadsheet.test.ts`, which
   * round-trips the written workbook. Do not read a green table as proof the
   * file is right about blanks.
   */
  excelRules?: readonly (readonly [string, string])[];
  divergence?: string;
}

export const FORMAT_EXPECTATIONS: readonly FormatExpectation[] = [
  {
    what: 'hours at their stored precision, untouched',
    measure: { format: 'hours' },
    chosen: {},
    text: [
      [372.5, '372.50'],
      [-7.25, '-7.25'],
      [0, '0.00'],
      [null, '—'],
    ],
    excel: '#,##0.00',
  },
  {
    what: 'currency to the cent, untouched',
    measure: { format: 'currency' },
    chosen: {},
    text: [
      [48210, '$48,210.00'],
      [-1234.56, '-$1,234.56'],
    ],
    excel: '$#,##0.00',
  },
  {
    what: 'a red minus keeps the sign',
    measure: { format: 'currency' },
    chosen: { negative: 'minusRed' },
    text: [[-1234.56, '-$1,234.56']],
    excel: '$#,##0.00;[Red]-$#,##0.00',
  },
  {
    what: "Excel's red-with-no-minus drops it, and colour carries the sign alone",
    measure: { format: 'currency' },
    chosen: { negative: 'redOnly' },
    text: [[-1234.56, '$1,234.56']],
    excel: '$#,##0.00;[Red]$#,##0.00',
  },
  {
    what: 'brackets, from what is PRINTED — -0.004 rounds to a bracketed zero on both surfaces',
    measure: { format: 'currency' },
    chosen: { negative: 'parensRed' },
    text: [
      [-1234.56, '($1,234.56)'],
      [-0.004, '($0.00)'],
      [0, '$0.00'],
    ],
    excel: '$#,##0.00;[Red]($#,##0.00)',
  },
  {
    what: 'a zero section forces the negative section to be spelled out',
    measure: { format: 'hours' },
    chosen: { zero: 'dash' },
    text: [
      [0, '-'],
      [-7.25, '-7.25'],
    ],
    excel: '#,##0.00;-#,##0.00;"-"',
  },
  {
    what: 'a blank zero is an empty third section',
    measure: { format: 'number' },
    chosen: { zero: 'blank', negative: 'parens' },
    text: [
      [0, ''],
      [-12, '(12)'],
    ],
    excel: '#,##0;(#,##0);',
  },
  {
    what: 'separators off, on both surfaces',
    measure: { format: 'hours' },
    chosen: { separators: false },
    text: [[1234.5, '1234.50']],
    excel: '0.00',
  },
  {
    what: "the report author's precision beats the unit's",
    measure: { format: 'currency' },
    chosen: { decimals: 0 },
    text: [[48210, '$48,210']],
    excel: '$#,##0',
  },
  {
    what: 'a declared precision PADS as well as truncates',
    measure: { format: 'hours', decimals: 4 },
    chosen: {},
    text: [[8, '8.0000']],
    excel: '#,##0.0000',
  },
  {
    what: 'the currency code in place of the symbol',
    measure: { format: 'currency' },
    chosen: { symbol: 'code' },
    // A NON-BREAKING SPACE, which is what `Intl` puts between a currency code
    // and its number. Spelled with the escape because the two are
    // indistinguishable on screen, and a test that fails saying
    // "expected 'USD 48,210.00' to be 'USD 48,210.00'" helps nobody.
    text: [[48210, 'USD\u00A048,210.00']],
    excel: '"USD "#,##0.00',
  },
  {
    what: 'a percent measure carries a RATIO, and reads as a percentage',
    measure: { format: 'percent' },
    chosen: { negative: 'parens' },
    text: [
      [0.612, '61.2%'],
      [-0.044, '(4.4%)'],
    ],
    excel: '#,##0.0%;(#,##0.0%)',
  },
  {
    what: 'a highlight paints the cell and leaves the digits alone',
    measure: { format: 'hours' },
    chosen: { rules: [{ op: 'lt', value: 0, tone: 'red' }] },
    // The number-format string is UNCHANGED by a rule, and that is the point:
    // a highlight is a second axis, not a variant spelling of the digits.
    text: [
      [-7.25, '-7.25'],
      [7.25, '7.25'],
    ],
    excel: '#,##0.00',
    tones: [
      [-7.25, 'red'],
      [0, null],
      [7.25, null],
    ],
    excelRules: [['lessThan', '0']],
  },
  {
    what: 'rules are ordered, and the FIRST match wins',
    measure: { format: 'hours' },
    chosen: {
      rules: [
        { op: 'lt', value: -40, tone: 'red' },
        { op: 'lt', value: 0, tone: 'amber' },
      ],
    },
    text: [[-50, '-50.00']],
    excel: '#,##0.00',
    // -50 satisfies BOTH rules. Order decides, not specificity and not
    // magnitude. The surfaces agree because the writer maps this order onto
    // Excel's ascending `priority` and Excel resolves a conflicting format
    // property in favour of the lower number — NOT because of `stopIfTrue`,
    // which ExcelJS never serialises. See `conditionalRules`.
    tones: [
      [-50, 'red'],
      [-10, 'amber'],
      [10, null],
    ],
    excelRules: [
      ['lessThan', '-40'],
      ['lessThan', '0'],
    ],
  },
  {
    what: 'a percent rule is written in percentage points and compared as a ratio',
    measure: { format: 'percent' },
    chosen: { rules: [{ op: 'gte', value: 100, tone: 'red' }] },
    text: [[1.1, '110.0%']],
    excel: '#,##0.0%',
    // The author typed 100. The cell holds 1.1. Both surfaces divide out.
    tones: [
      [1.1, 'red'],
      [1, 'red'],
      [0.99, null],
    ],
    excelRules: [['greaterThanOrEqual', '1']],
  },
  {
    what: 'the inclusive comparisons land ON the threshold, not beside it',
    measure: { format: 'currency' },
    chosen: {
      rules: [
        { op: 'gt', value: 1000, tone: 'green' },
        { op: 'lte', value: 0, tone: 'grey' },
      ],
    },
    text: [[1000.5, '$1,000.50']],
    excel: '$#,##0.00',
    // The boundary is the whole difference between `gt`/`gte` and `lt`/`lte`,
    // and it is the thing an author gets wrong first: 1000 is NOT greater than
    // 1000, and 0 IS less than or equal to 0.
    tones: [
      [1000.5, 'green'],
      [1000, null],
      [0, 'grey'],
      [-5, 'grey'],
      [500, null],
    ],
    excelRules: [
      ['greaterThan', '1000'],
      ['lessThanOrEqual', '0'],
    ],
  },
  {
    what: 'an absent cell takes no tone — "no rows matched" is not an overspend',
    measure: { format: 'hours' },
    chosen: { rules: [{ op: 'lt', value: 100, tone: 'red' }] },
    text: [[null, '—']],
    excel: '#,##0.00',
    // NOT in `tones`, which only carries numbers: the null case is asserted
    // directly in `value-format.test.ts`, because `toneFor` has to reject it
    // BEFORE any comparison — `null < 100` is true in JavaScript.
    excelRules: [['lessThan', '100']],
  },
  {
    what: 'thousands: the suffix is printed, the cell keeps the whole value',
    measure: { format: 'currency' },
    chosen: { scale: 'thousands', decimals: 1 },
    text: [
      [1234.5, '$1.2K'],
      [-48210, '-$48.2K'],
      // Below the divisor still reads in K — a column is one scale all the way
      // down, which is what Excel's trailing comma does too.
      [400, '$0.4K'],
      [0, '$0.0K'],
    ],
    excel: '$#,##0.0,"K"',
  },
  {
    what: 'a scale is ignored on a unit that does not offer it',
    measure: { format: 'hours' },
    // A spec can arrive hand-edited, or saved before the scale was limited to
    // currency. It reads as if the key were not there, on both surfaces.
    chosen: { scale: 'thousands' },
    text: [[1234.5, '1,234.50']],
    excel: '#,##0.00',
  },
  {
    what: 'millions on currency, with no decimals',
    measure: { format: 'currency' },
    chosen: { scale: 'millions', decimals: 0 },
    text: [[48_210_000, '$48M']],
    excel: '$#,##0,,"M"',
  },
  {
    what: 'a bracket closes AFTER the suffix — the sign wraps the magnitude',
    measure: { format: 'currency' },
    chosen: { scale: 'millions', decimals: 1, negative: 'parensRed', zero: 'dash' },
    text: [
      [-1_234_567, '($1.2M)'],
      // An EXACT zero takes the zero section; a value that merely rounds to
      // zero at this scale does not, on either surface.
      [0, '-'],
      [-400, '($0.0M)'],
    ],
    excel: '$#,##0.0,,"M";[Red]($#,##0.0,,"M");"-"',
  },
  {
    what: 'a scaled column still highlights against WHOLE values',
    measure: { format: 'currency' },
    chosen: { scale: 'thousands', decimals: 0, rules: [{ op: 'gt', value: 5000, tone: 'amber' }] },
    text: [[5400, '$5K']],
    excel: '$#,##0,"K"',
    // The author typed 5000, not 5. Changing the scale later must not silently
    // move every threshold on the column by a factor of a thousand.
    tones: [
      [5400, 'amber'],
      [5000, null],
    ],
    excelRules: [['greaterThan', '5000']],
  },
  {
    what: 'accounting aligns in the file and cannot in the browser',
    measure: { format: 'currency' },
    // RULES RIDE ALONG, and the pairing is the assertion: accounting fixes how
    // the digits are written and has no opinion on what is painted behind
    // them, so `ACCOUNTING_CONTROLS` has to keep offering `rules`. Dropping it
    // from that list silently empties `resolved.rules` for every Amount
    // column, and the identity check on `controls` elsewhere would not notice.
    chosen: { accounting: true, rules: [{ op: 'lt', value: 0, tone: 'red' }] },
    text: [
      [-1234.56, '($1,234.56)'],
      [0, '-'],
    ],
    tones: [
      [-1234.56, 'red'],
      [0, null],
      [1234.56, null],
    ],
    excelRules: [['lessThan', '0']],
    excel: '_($* #,##0.00_);_($* (#,##0.00);_($* "-"??_);_(@_)',
    divergence:
      'The `_(` padding and `*` column fill have no Intl equivalent: the file aligns the symbol and the decimal point down the column, the screen shows plain brackets. Values are identical.',
  },
  {
    what: 'accounting keeps its alignment at a scale',
    measure: { format: 'currency' },
    chosen: { accounting: true, scale: 'thousands', decimals: 0 },
    text: [
      [-48_210, '($48K)'],
      [0, '-'],
    ],
    excel: '_($* #,##0,"K"_);_($* (#,##0,"K");_($* "-"??_);_(@_)',
    divergence:
      'The `_(` padding and `*` column fill have no Intl equivalent: the file aligns the symbol and the decimal point down the column, the screen shows plain brackets. Values are identical.',
  },
];

/**
 * What a tone looks like in a WORKBOOK.
 *
 * The screen's copy of this lives in `report-kit/src/styles.css` as custom
 * properties, because only the browser has a theme to follow — these ARGBs are
 * the light-theme values of those tokens, and they are duplicated rather than
 * shared for the same reason the reset-period spans are duplicated in SQL: a
 * stylesheet cannot be imported into a Node process that writes a zip.
 *
 * Fill AND font, always as a pair. A fill chosen without the text colour that
 * sits on it is how a highlight turns into a redaction — `amber` in particular
 * needs dark text, where `red` at this weight does not.
 *
 * COMPUTED, NOT EYEBALLED. Each pair is the stylesheet's own mix evaluated
 * against the light theme: fill = hue at 26% over `#fff`, font = hue at 55%
 * over `#1b1d29`. The previous values were borrowed from other ramps rather
 * than mixed from these tokens — Material's red container, Tailwind's
 * `amber-100` — so the same rule painted one colour on screen and a
 * near-relation of it in the file, at a different strength. Deriving them is
 * what makes the two surfaces one palette, and it is what lets the ΔE floor
 * asserted over this table say anything about what the reader sees in the
 * browser. The hues are the ones named in `RULE_TONES`; `grey` mixes the
 * theme's `--rk-ink-soft` (`#5a5f73`) rather than a hue of its own.
 */
export const TONE_ARGB: Record<RuleTone, { fill: string; font: string }> = {
  red: { fill: 'FFEBC7C5', font: 'FF6F2223' },
  amber: { fill: 'FFF1E1BE', font: 'FF7B5915' },
  green: { fill: 'FFC2DECD', font: 'FF185334' },
  sky: { fill: 'FFBDDFF0', font: 'FF0D5680' },
  blue: { fill: 'FFC4D1F5', font: 'FF1C3889' },
  purple: { fill: 'FFE3CAFA', font: 'FF5D2993' },
  pink: { fill: 'FFEEC3D5', font: 'FF751A46' },
  grey: { fill: 'FFD4D5DB', font: 'FF3E4152' },
};

/** One Excel conditional-formatting rule, as the writer needs it. */
export interface ExcelRule {
  /** Excel's operator names; the writer maps these to formula operators. */
  operator: 'lessThan' | 'lessThanOrEqual' | 'greaterThan' | 'greaterThanOrEqual';
  /** The threshold, IN THE UNITS THE CELL STORES — see {@link toneFor}. */
  formula: string;
  fill: string;
  font: string;
}

const EXCEL_OPS: Record<FormatRule['op'], ExcelRule['operator']> = {
  lt: 'lessThan',
  lte: 'lessThanOrEqual',
  gt: 'greaterThan',
  gte: 'greaterThanOrEqual',
};

/**
 * The export's half of a highlight: one descriptor per rule for a measure's
 * column, which the spreadsheet writer renders as an Excel `expression` rule.
 *
 * CONDITIONAL FORMATTING, NOT A PER-CELL FILL, and the choice is the same one
 * `GridColumn` already made about number formats: a fill written onto every one
 * of a few thousand cells is a few thousand style records in the file stating
 * one repeated fact. A CF rule is a handful of records over a whole range, and
 * it stays live — a reader who edits an hour in the downloaded workbook sees
 * the highlight move, which a baked fill cannot do.
 *
 * ORDER IS PRIORITY. The writer assigns Excel's `priority` from this array's
 * index, and Excel resolves a conflicting format property in favour of the
 * lower number — so "first match wins" means the same thing in the file as it
 * does on screen, provided every rule sets both a fill and a font colour.
 * `TONE_ARGB` is what guarantees it does. (`stopIfTrue` would say this
 * explicitly; ExcelJS does not serialise it. See `conditionalRules`.)
 *
 * The threshold is converted OUT of display units here, exactly as `toneFor`
 * converts it, because the cell in the workbook holds the ratio too.
 */
export function excelRulesFor(f: ResolvedFormat): ExcelRule[] {
  const scale = f.format === 'percent' ? 100 : 1;
  return f.rules.map((rule) => ({
    operator: EXCEL_OPS[rule.op],
    formula: String(rule.value / scale),
    ...TONE_ARGB[rule.tone],
  }));
}
